// Sync engine — drains the Dexie outbox by replaying queued mutations against
// the server in FIFO order. Ported from sai-infra's features/offline/
// sync-engine.ts and adapted to Paradise's contracts.
//
// State machine, per op (docs/plan.md, Offline Resilience):
//
//   pending ──pick──▶ in_flight ──┬─ ok ───────────▶ done
//                                 ├─ offline/5xx ──▶ failed_transient ──backoff──▶ pending*
//                                 ├─ transient code▶ failed_transient ──backoff──▶ pending*
//                                 ├─ permanent code▶ failed_permanent  (needs a human)
//                                 └─ 401/403 ──────▶ (left pending; session layer logs out)
//
//   * a failed_transient row is re-picked once `lastAttemptAt + backoff[attempt]`
//     has elapsed; after six attempts THAT REACHED THE SERVER (`serverAttempts`)
//     it converts to failed_permanent. Failing to reach the server at all never
//     spends that budget — see the `offline` branch of settleResult.
//
// Error branching is on the canonical ErrorCode from @paradise/shared, NEVER on
// message text (coding-standards.md, Errors):
//   • `offline` (network / timeout / 5xx — from lib/api/http.ts) → transient.
//   • `error` with a code → isTransientErrorCode(): RATE_LIMITED / INTERNAL_ERROR
//     transient; VALIDATION_ERROR / CONFLICT / NOT_FOUND / … permanent.
//
// SEQUENTIAL drain (one op at a time) so causal order survives and two
// lead.creates hit the server's advisory-lock ref allocator in submit order.
import {
  CARD_BACK_FIELD,
  CARD_FRONT_FIELD,
  isTransientErrorCode,
  LEAD_PAYLOAD_FIELD,
  LeadWithItems,
  type CreateLeadInput
} from '@paradise/shared';

import { apiRequest, apiRequestMultipart, type ApiResult } from '@/lib/api/http';
import {
  db,
  type LeadCreatePayload,
  type LeadStageChangePayload,
  type LeadUpdatePayload,
  type OutboxRow
} from '@/lib/db';
import { useSessionStore } from '@/lib/auth/session';

import { subscribeConnectivity } from './connectivity';
import { refreshLeadsMirror } from './leads-mirror';
import { queueUpdateForSyncedLead, refreshSyncCounts, registerDrainTrigger } from './outbox';
import { useSyncStatus } from './sync-status-store';

// ── Constants ────────────────────────────────────────────────────────────────

const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 15_000, 60_000, 300_000];
const MAX_ATTEMPTS = RETRY_DELAYS_MS.length;

/**
 * Append a stored card blob (front or back) to the outgoing multipart form,
 * reconstructing a Blob from the ArrayBuffer only at send time. A missing blob
 * is not fatal — the lead's text is the valuable part.
 */
function appendCardBlob(
  form: FormData,
  field: string,
  blob: { bytes: ArrayBuffer; contentType: string } | undefined,
  filename: string
): void {
  if (blob) form.append(field, new Blob([blob.bytes], { type: blob.contentType }), filename);
}

// ── Module state ─────────────────────────────────────────────────────────────

let unsubConnectivity: (() => void) | null = null;
let visibilityHandler: (() => void) | null = null;
let started = false;
/** Singleflight — concurrent triggers (online + visibility + manual) coalesce. */
let drainInFlight: Promise<void> | null = null;
/**
 * A trigger arrived mid-pass, so one more pass is owed when it finishes.
 *
 * Dropping those triggers lost the queue outright: a pass stops and arms the
 * backoff timer, then a `visibilitychange` (constant on Android) lands before
 * `drainInFlight` clears — scheduleDrain cancels the armed timer, runDrain
 * returns the finishing pass, and nothing at all remains scheduled. That is the
 * "leads strand after a flap" failure; a manual Sync now always fixed it,
 * because the work was never broken, only unscheduled.
 */
let rerunRequested = false;
/** Backoff timer; cancelled when another trigger fires. */
let retryTimer: number | null = null;

// ── Public surface ───────────────────────────────────────────────────────────

export function startSyncEngine(): void {
  if (started) return;
  started = true;

  // Enqueue seams nudge us directly (no import cycle — see outbox.ts).
  registerDrainTrigger(() => scheduleDrain('enqueue'));

  unsubConnectivity = subscribeConnectivity(online => {
    if (online) void clearOfflineBackoffThenDrain();
    else void refreshSyncCounts();
  });
  visibilityHandler = (): void => {
    if (document.visibilityState === 'visible') scheduleDrain('visibility');
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  void refreshSyncCounts();
  scheduleDrain('boot');
}

export function stopSyncEngine(): void {
  if (!started) return;
  started = false;
  unsubConnectivity?.();
  unsubConnectivity = null;
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
}

/**
 * Manual "Sync now" — the button on the /sync screen and readiness panel.
 *
 * Clears the backoff first. A human tapping Sync now is overriding the
 * schedule, and `pickNextOp` gates a `failed_transient` row on
 * `lastAttemptAt + backoff`: without this the button picked up nothing at all
 * for up to five minutes and looked simply broken, which is exactly how it was
 * reported from the field.
 */
export async function flushNow(): Promise<void> {
  await db.outbox.where('status').equals('failed_transient').modify({ attempts: 0, lastAttemptAt: null });
  await runDrain('manual');
}

// ── Orchestration ────────────────────────────────────────────────────────────

function scheduleDrain(_reason: string): void {
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
  void runDrain(_reason);
}

/**
 * Connectivity just returned, so drop the backoff earned while there was none.
 *
 * `attempts` climbs on every failed try, including the futile ones a device
 * makes in a dead zone, and it indexes the backoff ladder — a phone offline for
 * a few minutes reconnects with its queue sitting on the 300s step, so the
 * first real attempt is five minutes after the salesperson is back on wifi,
 * with nothing on screen to say so.
 *
 * Only rows whose last failure was `offline:*`. A RATE_LIMITED row keeps its
 * backoff: that one earned it from the server, and a flappy hall wifi would
 * otherwise re-fire it on every edge.
 */
async function clearOfflineBackoffThenDrain(): Promise<void> {
  await db.outbox
    .where('status')
    .equals('failed_transient')
    .filter(row => row.lastErrorCode?.startsWith('offline:') ?? false)
    .modify({ attempts: 0, lastAttemptAt: null });
  scheduleDrain('connectivity');
}

function runDrain(reason: string): Promise<void> {
  if (drainInFlight) {
    // Never drop the trigger — see `rerunRequested`. N triggers during one pass
    // still buy exactly one re-run, since the flag resets per iteration.
    rerunRequested = true;
    return drainInFlight;
  }
  drainInFlight = (async () => {
    try {
      do {
        rerunRequested = false;
        await drainOnce(reason);
      } while (rerunRequested);
    } finally {
      drainInFlight = null;
      rerunRequested = false;
    }
  })();
  return drainInFlight;
}

/** Set by a delivered op; drainOnce does ONE mirror refresh per pass if true. */
let mirrorDirty = false;

async function drainOnce(_reason: string): Promise<void> {
  // A row can be stranded `in_flight` if the tab/PWA was killed after
  // processOp flipped it but before the response settled its status — it
  // would otherwise never be re-picked (pickNextOp only looks at `pending`
  // and `failed_transient`) and never surface on /sync either. Reset it back
  // to `pending` so it re-drains; the server's clientUuid idempotency makes a
  // re-submit a safe no-op. A row genuinely in flight THIS session is never
  // seen here — drainInFlight singleflights the pass, and processOp always
  // resolves the row's status (via settleResult) before this loop continues.
  await recoverStrandedInFlight();
  await refreshSyncCounts();
  for (;;) {
    const next = await pickNextOp();
    if (!next) break;
    useSyncStatus.getState().setSyncing(true);
    const outcome = await runOpGuarded(next);
    if (outcome === 'stop') {
      // The transport or the session is the problem, not this op — every other
      // queued op would fail the same way, and on a dead network each attempt
      // costs a full 10s timeout. Back off and stop the pass.
      if (next.status !== 'done') scheduleRetry(next);
      break;
    }
    // 'continue' — this op is resolved (done, parked, or skipped for its own
    // reasons). Move on: one bad op must never hold the queue behind it.
  }
  // One mirror refresh per drain if anything was delivered — the per-op
  // db.leads.put already folded each synced lead; this catches server-side
  // changes (e.g. another device's leads) without a GET /leads per op.
  const delivered = mirrorDirty;
  if (mirrorDirty) {
    mirrorDirty = false;
    await refreshLeadsMirror();
  }
  await refreshSyncCounts();
  useSyncStatus.getState().setSyncing(false);
  // `lastSyncAt` documents "most recent SUCCESSFUL drain" — an empty pass or
  // one where every op failed delivered nothing, so it must not be stamped.
  if (delivered) {
    useSyncStatus.getState().markSyncedNow();
  }
}

/** See the comment at its call site in drainOnce. */
async function recoverStrandedInFlight(): Promise<void> {
  await db.outbox.where('status').equals('in_flight').modify({ status: 'pending' });
}

async function pickNextOp(): Promise<OutboxRow | null> {
  const candidates = await db.outbox.where('status').anyOf(['pending', 'failed_transient']).sortBy('createdAt');
  for (const op of candidates) {
    if (op.status === 'pending') return op;
    // failed_transient — gate on backoff before re-picking.
    const wait = RETRY_DELAYS_MS[Math.min(op.attempts - 1, MAX_ATTEMPTS - 1)] ?? 300_000;
    if ((op.lastAttemptAt ?? 0) + wait <= Date.now()) return op;
  }
  return null;
}

type OpOutcome = 'continue' | 'stop';

/**
 * Nothing processOp throws may escape into drainOnce. An onOk callback runs a
 * Dexie transaction over five tables and dereferences a server payload; an
 * exception from either used to abort the whole pass AND strand the row
 * `in_flight`. Transient, because a throw says nothing about the request's
 * validity — the op keeps its retry budget and the pass carries on.
 */
async function runOpGuarded(op: OutboxRow): Promise<OpOutcome> {
  try {
    return await processOp(op);
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'Unexpected error while syncing';
    await markTransient(op, 'INTERNAL_ERROR', message).catch(() => undefined);
    return 'continue';
  }
}

async function processOp(op: OutboxRow): Promise<OpOutcome> {
  // Flip to in_flight + bump attempts BEFORE firing the network. A crash mid-
  // request leaves the row re-picked on next boot; the clientUuid idempotency
  // key makes the double-submit a safe no-op.
  await db.outbox.update(op.clientUuid, { status: 'in_flight', attempts: op.attempts + 1, lastAttemptAt: Date.now() });
  const attempted: OutboxRow = { ...op, attempts: op.attempts + 1 };

  switch (op.kind) {
    case 'lead.create':
      return runLeadCreate(attempted, op.payload as LeadCreatePayload);
    case 'lead.update':
      return runLeadUpdate(attempted, op.payload as LeadUpdatePayload);
    case 'lead.stageChange':
      return runLeadStageChange(attempted, op.payload as LeadStageChangePayload);
  }
}

// ── Per-kind drains ──────────────────────────────────────────────────────────

async function runLeadCreate(op: OutboxRow, payload: LeadCreatePayload): Promise<OpOutcome> {
  const form = new FormData();
  form.append(LEAD_PAYLOAD_FIELD, JSON.stringify(payload.input));
  // A missing card blob is not fatal: the lead's text is the valuable part, so
  // submit without it rather than parking the whole conversation.
  if (op.hasCard) appendCardBlob(form, CARD_FRONT_FIELD, await db.cardBlobs.get(op.clientUuid), 'card-front.jpg');
  if (op.hasCardBack) appendCardBlob(form, CARD_BACK_FIELD, await db.cardBackBlobs.get(op.clientUuid), 'card-back.jpg');

  // `schema` is load-bearing here: onOk dereferences the response inside a
  // Dexie transaction, and an unvalidated `as T` there poisons the queue.
  const result = await apiRequestMultipart<LeadWithItems>('/api/leads', form, { schema: LeadWithItems });
  return settleResult(op, result, async lead => {
    await db.transaction('rw', db.outbox, db.leadsDraft, db.cardBlobs, db.cardBackBlobs, db.leads, async () => {
      // Fold the canonical row straight into the mirror so the board flips from
      // "Pending" to its ref without waiting for the next full mirror fetch.
      // First, so the reconcile below can read the server's own stage from it.
      await db.leads.put(lead);
      await reconcileAmendedCreate(op.clientUuid, lead);
      await db.outbox.update(op.clientUuid, { status: 'done', lastError: null, lastErrorCode: null });
      await db.leadsDraft.update(op.clientUuid, {
        status: 'synced',
        ref: lead.ref,
        serverId: lead.id,
        updatedAt: Date.now()
      });
      // The card bytes have served their purpose; drop them from the device.
      await db.cardBlobs.delete(op.clientUuid);
      await db.cardBackBlobs.delete(op.clientUuid);
    });
  });
}

/**
 * The one case where a delivered `lead.create` has NOT delivered what the
 * device holds.
 *
 * `POST /api/leads` is idempotent by returning the existing lead for a known
 * `clientUuid` — it never applies the incoming payload to it (apps/api
 * `routes/leads.ts`, `insertLead`). So an entry edited via `amendLeadCreate`
 * can come back `ok` carrying the PRE-edit lead, two ways: the edit landed
 * while the request was in flight, or an earlier attempt was accepted and its
 * response was lost, and the replay quietly returned that older row. Either
 * way the amendment is gone unless it is re-expressed as an update.
 *
 * Only entries that were actually amended get compared, so the ordinary create
 * path never runs this.
 */
async function reconcileAmendedCreate(clientUuid: string, lead: LeadWithItems): Promise<void> {
  const fresh = await db.outbox.get(clientUuid);
  if (!fresh?.amendedAt || fresh.kind !== 'lead.create') return;
  const intended = (fresh.payload as LeadCreatePayload).input;
  if (matchesLead(lead, intended)) return;
  await queueUpdateForSyncedLead(lead.id, clientUuid, intended);
}

/** Stable key for an item line — order-insensitive, since the server does not order `items`. */
function itemKey(item: {
  productId: string;
  quantity: number;
  quotedPrice: string;
  listPriceSnapshot?: string | null;
  productNameSnapshot: string;
  note?: string | null;
}): string {
  return [
    item.productId,
    String(item.quantity),
    item.quotedPrice,
    item.listPriceSnapshot ?? '',
    item.productNameSnapshot,
    item.note ?? ''
  ].join(' ');
}

/** Does the server's lead already say everything the queued input wanted it to say? */
function matchesLead(lead: LeadWithItems, input: CreateLeadInput): boolean {
  const scalarsMatch =
    lead.company === input.company &&
    (lead.contactName ?? null) === (input.contactName ?? null) &&
    (lead.phone ?? null) === (input.phone ?? null) &&
    (lead.email ?? null) === (input.email ?? null) &&
    (lead.country ?? null) === (input.country ?? null) &&
    (lead.notes ?? null) === (input.notes ?? null) &&
    (lead.followUpDate ?? null) === (input.followUpDate ?? null) &&
    (lead.paymentMode ?? null) === (input.paymentMode ?? null) &&
    (lead.advance ?? null) === (input.advance ?? null) &&
    lead.stage === (input.stage ?? lead.stage);
  if (!scalarsMatch) return false;
  if (lead.items.length !== input.items.length) return false;
  const onServer = lead.items.map(itemKey).sort();
  const intended = input.items.map(itemKey).sort();
  return onServer.every((key, index) => key === intended[index]);
}

async function runLeadUpdate(op: OutboxRow, payload: LeadUpdatePayload): Promise<OpOutcome> {
  const path = `/api/leads/${payload.leadId}`;
  let result: ApiResult<LeadWithItems>;
  if (op.hasCard || op.hasCardBack) {
    const form = new FormData();
    form.append(LEAD_PAYLOAD_FIELD, JSON.stringify(payload.input));
    if (op.hasCard) appendCardBlob(form, CARD_FRONT_FIELD, await db.cardBlobs.get(op.clientUuid), 'card-front.jpg');
    if (op.hasCardBack)
      appendCardBlob(form, CARD_BACK_FIELD, await db.cardBackBlobs.get(op.clientUuid), 'card-back.jpg');
    result = await apiRequestMultipart<LeadWithItems>(path, form, { method: 'PATCH', schema: LeadWithItems });
  } else {
    result = await apiRequest<LeadWithItems>(path, { method: 'PATCH', body: payload.input, schema: LeadWithItems });
  }
  return settleResult(op, result, async lead => {
    await db.transaction('rw', db.outbox, db.cardBlobs, db.cardBackBlobs, db.leads, async () => {
      await db.outbox.update(op.clientUuid, { status: 'done', lastError: null, lastErrorCode: null });
      await db.cardBlobs.delete(op.clientUuid);
      await db.cardBackBlobs.delete(op.clientUuid);
      await db.leads.put(lead);
    });
  });
}

async function runLeadStageChange(op: OutboxRow, payload: LeadStageChangePayload): Promise<OpOutcome> {
  const result = await apiRequest<LeadWithItems>(`/api/leads/${payload.leadId}/stage`, {
    method: 'POST',
    body: payload.input,
    schema: LeadWithItems
  });
  return settleResult(op, result, async lead => {
    await db.transaction('rw', db.outbox, db.leads, async () => {
      await db.outbox.update(op.clientUuid, { status: 'done', lastError: null, lastErrorCode: null });
      await db.leads.put(lead);
    });
  });
}

// ── Result → state transition ────────────────────────────────────────────────

/**
 * Maps an ApiResult onto the outbox state machine. `onOk` performs the op's
 * bespoke success writes (draft flip, mirror fold, card cleanup).
 */
async function settleResult<T>(
  op: OutboxRow,
  result: ApiResult<T>,
  onOk: (data: T) => Promise<void>
): Promise<OpOutcome> {
  switch (result.kind) {
    case 'ok':
      await onOk(result.data);
      // onOk already folded this lead into db.leads; just mark the mirror dirty
      // so drainOnce does a single refresh after the pass (not one per op).
      mirrorDirty = true;
      return 'continue';

    case 'offline':
      // Not reaching the server says nothing about the request's validity, so
      // it must NOT consume the attempt budget. It used to: a device offline
      // for even half a minute burned all six attempts on backoff alone and
      // parked its captured leads as failed_permanent — "needs a human" for a
      // lead whose only sin was being written in a dead zone. Retries forever
      // instead, capped by the 300s top of the backoff ladder.
      await markTransient(op, `offline:${result.reason}`, `Could not reach the server (${result.reason})`, {
        escalate: false
      });
      return 'stop';

    case 'error':
      if (isTransientErrorCode(result.code)) {
        const status = await markTransient(op, result.code, result.message);
        if (status === 'failed_permanent') return 'continue';
        // RATE_LIMITED is the only transient code about the CONNECTION rather
        // than this op, so it is the only one that stops the pass. INTERNAL_ERROR
        // is op-specific until proven otherwise — a payload the server chokes on
        // must not hold the queue behind it. pickNextOp's backoff gate still
        // caps each op at one attempt per pass.
        return result.code === 'RATE_LIMITED' ? 'stop' : 'continue';
      }
      // VALIDATION_ERROR / CONFLICT / NOT_FOUND / … — retrying the same request
      // cannot succeed. Park it for the /sync recovery screen.
      await markPermanent(op, result.code, result.message);
      return 'continue';

    case 'unauthenticated':
      // 401 / 403 / null-body. The op stays pending (not stuck in_flight), and
      // this engine does NOT itself decide logout — but it MUST kick the session
      // layer to re-check. The only other revalidation triggers are the `online`
      // and `visibilitychange` events (auth/session.ts); neither fires when a
      // session is revoked server-side while the app sits foregrounded on wifi,
      // so without this a revoked handset reads as "still signed in" while its
      // leads silently never sync. revalidate() hits the authoritative
      // /get-session: a real 401/403 there flips the session to unauthenticated
      // (the router guard redirects to /login), a transient blip leaves it
      // untouched — consistent with "only a real 401/403 clears the session".
      await db.outbox.update(op.clientUuid, { status: 'pending' });
      useSyncStatus.getState().setLastError('Signed out — sign in to sync');
      void useSessionStore.getState().revalidate();
      return 'stop';
  }
}

/**
 * Returns the row's resulting status so the caller can decide continue-vs-stop.
 * `escalate: false` means the attempt never reached the server, so it neither
 * spends the give-up budget nor can park the op (see the `offline` branch).
 */
async function markTransient(
  op: OutboxRow,
  code: string,
  message: string,
  { escalate = true }: { escalate?: boolean } = {}
): Promise<'failed_transient' | 'failed_permanent'> {
  const serverAttempts = (op.serverAttempts ?? 0) + (escalate ? 1 : 0);
  if (escalate && serverAttempts >= MAX_ATTEMPTS) {
    await markPermanent(op, code, `${message} — gave up after ${String(MAX_ATTEMPTS)} attempts`);
    return 'failed_permanent';
  }
  await db.outbox.update(op.clientUuid, {
    status: 'failed_transient',
    serverAttempts,
    lastErrorCode: code,
    lastError: message
  });
  useSyncStatus.getState().setLastError(message);
  return 'failed_transient';
}

async function markPermanent(op: OutboxRow, code: string, message: string): Promise<void> {
  await db.transaction('rw', db.outbox, db.leadsDraft, async () => {
    await db.outbox.update(op.clientUuid, { status: 'failed_permanent', lastErrorCode: code, lastError: message });
    await db.leadsDraft.update(op.clientUuid, { status: 'failed', updatedAt: Date.now() });
  });
  useSyncStatus.getState().setLastError(message);
}

function scheduleRetry(op: OutboxRow): void {
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  const idx = Math.min(op.attempts, MAX_ATTEMPTS - 1);
  const delay = RETRY_DELAYS_MS[idx] ?? 300_000;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    void runDrain('retry-backoff');
  }, delay);
}
